import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ProfilePictureReminderDialog } from '.';
import { userprofileProfileImageReminderLaterRequestAction } from '../../../data/actions/foundation';

/**
 * Decides whether the profile-picture reminder should pop up after login and
 * wires the actions. Shows once per login when the user has no avatar and has
 * neither snoozed ("Später erinnern") nor opted out.
 */
export const ConnectedProfilePictureReminder: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const auth = useSelector(state => state.foundation?.auth);
  const anon = useSelector(state => state.foundation?.anon);
  const updateTermsRules = useSelector(state => state.foundation?.updatetermsrules);
  const setTermsRules = useSelector(state => state.foundation?.settermsrules);
  const conferencingVisible = useSelector(state => state.conferencing?.visible);
  const profile = useSelector(state => state.foundation?.profile);
  // Server-side feature flag (UWAO_MTO_ENABLE_PROFILE_IMAGE_REMINDER) surfaced via startup.
  const reminderEnabled = useSelector(state => state.foundation?.profileImageReminderEnabled);
  const [open, setOpen] = useState(false);
  // Which user the reminder was already shown to in this page load, so a dismiss is not
  // undone by the next re-render. This component is mounted permanently in AppFrame and
  // never unmounts on logout, so it has to be reset when the session ends — otherwise a
  // logout and login in the same tab would not remind again.
  const shownForUserId = useRef<string | null>(null);

  const eligible =
    !!reminderEnabled &&
    !!auth &&
    !anon &&
    !updateTermsRules &&
    !setTermsRules &&
    !conferencingVisible &&
    !!profile?.userId &&
    !profile?.avatar &&
    !profile?.profileImageReminderNever &&
    !profile?.profileImageReminderLater;

  useEffect(() => {
    // Session ended (logout, deauth, expiry): forget who was reminded, so the next
    // login starts over.
    if (!auth || anon) {
      shownForUserId.current = null;
    }
  }, [auth, anon]);

  useEffect(() => {
    if (eligible && profile?.userId && shownForUserId.current !== profile.userId) {
      shownForUserId.current = profile.userId;
      setOpen(true);
    }
  }, [eligible, profile]);

  const handleRemindLater = () => {
    // Only the explicit "Später erinnern" button snoozes the reminder for half a year.
    dispatch(userprofileProfileImageReminderLaterRequestAction());
    setOpen(false);
  };

  const handleDismiss = () => {
    // Close icon / outside click: dismiss for this session without snoozing — no timestamp set.
    setOpen(false);
  };

  const handleUpload = () => {
    setOpen(false);
    history.push('/user/profile?edit=avatar');
  };

  return <ProfilePictureReminderDialog open={open} onUpload={handleUpload} onRemindLater={handleRemindLater} onDismiss={handleDismiss} />;
};
